/**
 * Pure combat helpers — damage chain and hit-chance.
 *
 * MECHANICS §1.2 (v1): damage pipeline is Shields → Armor% → Hull.
 *  - Shields absorb 1:1 until depleted.
 *  - Armor negates a fixed fraction of post-shield damage (armor ∈ [0, 1)).
 *  - Hull takes the remainder.
 *
 * Hit-chance: HitChance = Accuracy × (1 − Dodge), clamped to [0, 1].
 *
 * These functions are kept side-effect-free so the sim layer can compose them
 * and unit tests can exercise edge cases directly.
 */

/**
 * Apply `rawDamage` through the Shields→Armor→Hull chain.
 * Returns the updated values; does not mutate inputs.
 */
export function resolveDamage(
  rawDamage: number,
  shields: number,
  armor: number,
  hull: number,
): { shields: number; hull: number; hullDamage: number; shieldDamage: number } {
  const dmg = Math.max(0, rawDamage);
  const shieldDamage = Math.min(shields, dmg);
  const afterShields = dmg - shieldDamage;
  const armorFactor = Math.max(0, Math.min(1, armor));
  const hullDamage = afterShields * (1 - armorFactor);
  return {
    shields: shields - shieldDamage,
    hull: Math.max(0, hull - hullDamage),
    hullDamage,
    shieldDamage,
  };
}

/** HitChance = Accuracy × (1 − Dodge), clamped to [0, 1]. */
export function hitChance(accuracy: number, dodge: number): number {
  const a = Math.max(0, Math.min(1, accuracy));
  const d = Math.max(0, Math.min(1, dodge));
  const v = a * (1 - d);
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
